/**
 * M.2 — NIPE tool: applySkill
 *
 * Read-only. Returns a Skill body + the caller-supplied args so the model
 * can follow its instructions on the next turn. The body becomes part of
 * the conversation via the tool-result content.
 *
 * Future iteration: execute the Skill body as a sub-prompt with the
 * focused note attached.
 */

import type { NipeTool } from './types';

import type { GetSkillContext } from './getSkill';

export type ApplySkillContext = GetSkillContext;

export const applySkillTool: NipeTool<ApplySkillContext> = {
  schema: {
    type: 'function',
    function: {
      name: 'applySkill',
      description:
        'Run a Skill against the current matter/note context. Returns the Skill body so you can follow its instructions in your next turn.',
      parameters: {
        type: 'object',
        properties: {
          name: { type: 'string', description: 'Skill name.' },
          args: {
            type: 'object',
            description: 'Free-form argument object the Skill needs.',
          },
        },
        required: ['name'],
      },
    },
  },
  mutatesRemote: false,
  requiresApproval: false,
  handler: async (args, ctx) => {
    const skillName = typeof args.name === 'string' ? args.name : '';
    if (!skillName) return { error: 'name required' };
    const skill = await ctx.getSkill(skillName);
    if (!skill) return { error: `Skill "${skillName}" not found` };
    return {
      name: skill.name,
      body: skill.body,
      args: args.args ?? null,
      note: 'Apply this Skill: follow its instructions using the args provided.',
    };
  },
};
